import React, { useRef, useEffect, useState, useCallback } from 'react';

interface AngleSelection {
  horizontal_angle: number;
  vertical_angle: number;
  zoom: number;
}

interface AngleSelector3DProps {
  imageUrl: string;
  onSelectionChange: (selection: AngleSelection) => void;
  disabled?: boolean;
}

// 3D math helpers
interface Vec3 { x: number; y: number; z: number; }

const degToRad = (d: number) => d * Math.PI / 180;

const rotateY = (v: Vec3, angle: number): Vec3 => {
  const c = Math.cos(angle), s = Math.sin(angle);
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c };
};

const rotateX = (v: Vec3, angle: number): Vec3 => {
  const c = Math.cos(angle), s = Math.sin(angle);
  return { x: v.x, y: v.y * c - v.z * s, z: v.y * s + v.z * c };
};

// Draw image mapped to a quad using two affine-transformed triangles
// pts: [topLeft, topRight, bottomRight, bottomLeft] in logical coords
// img: source image
const drawImageQuad = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  pts: { x: number; y: number }[]
) => {
  const iw = img.width;
  const ih = img.height;

  // Triangle 1: top-left(0), top-right(1), bottom-left(3)
  // Maps from image coords (0,0), (iw,0), (0,ih) to screen pts[0], pts[1], pts[3]
  drawTriangle(ctx, img, 0, 0, iw, 0, 0, ih, pts[0].x, pts[0].y, pts[1].x, pts[1].y, pts[3].x, pts[3].y);
  // Triangle 2: top-right(1), bottom-right(2), bottom-left(3)
  // Maps from image coords (iw,0), (iw,ih), (0,ih) to screen pts[1], pts[2], pts[3]
  drawTriangle(ctx, img, iw, 0, iw, ih, 0, ih, pts[1].x, pts[1].y, pts[2].x, pts[2].y, pts[3].x, pts[3].y);
};

const drawTriangle = (
  ctx: CanvasRenderingContext2D, img: HTMLImageElement,
  sx0: number, sy0: number, sx1: number, sy1: number, sx2: number, sy2: number,
  dx0: number, dy0: number, dx1: number, dy1: number, dx2: number, dy2: number
) => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(dx0, dy0);
  ctx.lineTo(dx1, dy1);
  ctx.lineTo(dx2, dy2);
  ctx.closePath();
  ctx.clip();

  // Solve the 2x3 affine matrix that maps source triangle to dest triangle
  // [a c e] [sx]   [dx]
  // [b d f] [sy] = [dy]
  //         [ 1]
  const denom = sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1);
  if (Math.abs(denom) < 1e-10) { ctx.restore(); return; }
  const inv = 1 / denom;

  const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) * inv;
  const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) * inv;
  const c = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) * inv;
  const d = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) * inv;
  const e = (dx0 * (sx1 * sy2 - sx2 * sy1) + dx1 * (sx2 * sy0 - sx0 * sy2) + dx2 * (sx0 * sy1 - sx1 * sy0)) * inv;
  const f = (dy0 * (sx1 * sy2 - sx2 * sy1) + dy1 * (sx2 * sy0 - sx0 * sy2) + dy2 * (sx0 * sy1 - sx1 * sy0)) * inv;

  // The current transform is already DPR-scaled. We need to compose our affine with it.
  // ctx.transform multiplies the current matrix by the given one, so this works correctly.
  ctx.transform(a, b, c, d, e, f);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
};

const project = (v: Vec3, w: number, h: number, fov: number): { x: number; y: number; scale: number } | null => {
  const d = fov;
  const z = v.z + d + 3;
  if (z <= 0.1) return null;
  const scale = d / z;
  // Multiply by min dimension to convert 3D units to meaningful pixel offsets
  const pixelScale = Math.min(w, h) * 0.28;
  return { x: w / 2 + v.x * scale * pixelScale, y: h / 2 - v.y * scale * pixelScale, scale };
};

// Generate camera positions on a hemisphere around the image
const BASE_RADIUS = 2.2;
const generateCameraPoints = (): { pos: Vec3; hAngle: number; vAngle: number; zoom: number; label: string }[] => {
  const points: { pos: Vec3; hAngle: number; vAngle: number; zoom: number; label: string }[] = [];
  const radius = BASE_RADIUS;

  // Horizontal angles: 0 (front), 45, 90, 135, 180, 225, 270, 315
  const hAngles = [0, 45, 90, 135, 180, 225, 270, 315];
  // Vertical angles: -30 (low), 0 (eye-level), 30 (elevated), 60 (high)
  const vAngles = [-30, 0, 30, 60];

  const hLabels: Record<number, string> = { 0: 'Frente', 45: 'Frente-Derecha', 90: 'Derecha', 135: 'Atrás-Derecha', 180: 'Atrás', 225: 'Atrás-Izquierda', 270: 'Izquierda', 315: 'Frente-Izquierda' };
  const vLabels: Record<string, string> = { '-30': 'Contrapicado', '0': 'Nivel', '30': 'Elevado', '60': 'Aéreo' };

  for (const h of hAngles) {
    for (const v of vAngles) {
      const hRad = degToRad(h);
      const vRad = degToRad(v);
      const x = radius * Math.cos(vRad) * Math.sin(hRad);
      const y = radius * Math.sin(vRad);
      const z = -radius * Math.cos(vRad) * Math.cos(hRad);
      points.push({
        pos: { x, y, z },
        hAngle: h,
        vAngle: v,
        zoom: 5,
        label: `${hLabels[h]} · ${vLabels[String(v)]}`
      });
    }
  }

  return points;
};

const CAMERA_POINTS = generateCameraPoints();

// Zoom maps 0-10 to a radius scale factor for the selected point
// 0=far (1.45x), 5=normal (1.0x), 10=close (0.64x)
const zoomToRadiusScale = (zoom: number): number => {
  return 1 + (5 - zoom) * 0.09;
};

const AngleSelector3D: React.FC<AngleSelector3DProps> = ({ imageUrl, onSelectionChange, disabled }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(5);
  const [orbitAngleY, setOrbitAngleY] = useState(0.5);
  const [orbitAngleX, setOrbitAngleX] = useState(-0.3);
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const animFrame = useRef<number>(0);

  // Load image — try without crossOrigin first (Supabase signed URLs may block CORS canvas)
  useEffect(() => {
    setImgLoaded(false);
    imgRef.current = null;
    const img = new Image();
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.onerror = () => {
      // Retry with crossOrigin as fallback
      const img2 = new Image();
      img2.crossOrigin = 'anonymous';
      img2.onload = () => { imgRef.current = img2; setImgLoaded(true); };
      img2.src = imageUrl;
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Use logical (CSS) dimensions, not DPI-scaled canvas pixels
    const dpr = window.devicePixelRatio || 1;
    const w = parseFloat(canvas.dataset.logicalWidth || String(canvas.width / dpr));
    const h = parseFloat(canvas.dataset.logicalHeight || String(canvas.height / dpr));
    const fov = 4;

    // Reset transform and apply DPR scaling each frame
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // Draw subtle grid on the ground plane
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 0.8;
    for (let i = -5; i <= 5; i++) {
      const p1 = project(rotateX(rotateY({ x: i * 0.5, y: -0.7, z: -2.5 }, orbitAngleY), orbitAngleX), w, h, fov);
      const p2 = project(rotateX(rotateY({ x: i * 0.5, y: -0.7, z: 2.5 }, orbitAngleY), orbitAngleX), w, h, fov);
      if (p1 && p2) { ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke(); }
      const p3 = project(rotateX(rotateY({ x: -2.5, y: -0.7, z: i * 0.5 }, orbitAngleY), orbitAngleX), w, h, fov);
      const p4 = project(rotateX(rotateY({ x: 2.5, y: -0.7, z: i * 0.5 }, orbitAngleY), orbitAngleX), w, h, fov);
      if (p3 && p4) { ctx.beginPath(); ctx.moveTo(p3.x, p3.y); ctx.lineTo(p4.x, p4.y); ctx.stroke(); }
    }
    ctx.globalAlpha = 1;

    // Draw the image as a 3D plane in the center
    {
      const img = imgRef.current;
      const aspect = (img && imgLoaded) ? img.width / img.height : 1;
      const planeH = 1.2;
      const planeW = planeH * aspect;
      const hw = planeW / 2;
      const hh = planeH / 2;

      // 4 corners of the image plane (3D)
      const corners3D: Vec3[] = [
        { x: -hw, y: hh, z: 0 },   // top-left
        { x: hw, y: hh, z: 0 },    // top-right
        { x: hw, y: -hh, z: 0 },   // bottom-right
        { x: -hw, y: -hh, z: 0 },  // bottom-left
      ];

      const projected = corners3D.map(c => {
        const r = rotateX(rotateY(c, orbitAngleY), orbitAngleX);
        return project(r, w, h, fov);
      });

      if (projected.every(p => p !== null)) {
        const pts = projected as { x: number; y: number; scale: number }[];

        // Determine if we're looking at the front face using the 3D normal
        // The plane normal is (0,0,1). After rotation, check if the Z component faces the camera (negative Z = towards camera)
        const normal: Vec3 = { x: 0, y: 0, z: 1 };
        const rotatedNormal = rotateX(rotateY(normal, orbitAngleY), orbitAngleX);
        const isFrontFace = rotatedNormal.z > 0;

        // Draw shadow under the image
        ctx.save();
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y + 6);
        ctx.lineTo(pts[1].x, pts[1].y + 6);
        ctx.lineTo(pts[2].x, pts[2].y + 6);
        ctx.lineTo(pts[3].x, pts[3].y + 6);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        if (!isFrontFace) {
          // Back face: solid gray
          ctx.save();
          ctx.fillStyle = '#d1d5db';
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          ctx.lineTo(pts[1].x, pts[1].y);
          ctx.lineTo(pts[2].x, pts[2].y);
          ctx.lineTo(pts[3].x, pts[3].y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        } else if (!img || !imgLoaded) {
          // Front face but image not loaded yet: light placeholder
          ctx.save();
          ctx.fillStyle = '#e2e8f0';
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          ctx.lineTo(pts[1].x, pts[1].y);
          ctx.lineTo(pts[2].x, pts[2].y);
          ctx.lineTo(pts[3].x, pts[3].y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        } else {
          // Front face: draw image mapped to the perspective quad using two affine triangles
          drawImageQuad(ctx, img, pts);
        }

        // Border around image
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        ctx.lineTo(pts[1].x, pts[1].y);
        ctx.lineTo(pts[2].x, pts[2].y);
        ctx.lineTo(pts[3].x, pts[3].y);
        ctx.closePath();
        ctx.stroke();
      }
    }

    // Collect projected points with depth for sorting
    const projectedPoints: { idx: number; px: number; py: number; scale: number; depth: number }[] = [];
    const zoomScale = zoomToRadiusScale(zoomLevel);

    for (let i = 0; i < CAMERA_POINTS.length; i++) {
      const cp = CAMERA_POINTS[i];
      // Only scale the selected point's position based on zoom
      const pos = (i === selectedIndex)
        ? { x: cp.pos.x * zoomScale, y: cp.pos.y * zoomScale, z: cp.pos.z * zoomScale }
        : cp.pos;
      const rotated = rotateX(rotateY(pos, orbitAngleY), orbitAngleX);
      const p = project(rotated, w, h, fov);
      if (p) {
        projectedPoints.push({ idx: i, px: p.x, py: p.y, scale: p.scale, depth: rotated.z });
      }
    }

    // Sort by depth (far first)
    projectedPoints.sort((a, b) => b.depth - a.depth);

    // Draw ghost point at original (zoom=0) position and dashed line to zoomed point
    if (selectedIndex !== null && zoomLevel !== 5) {
      const cpBase = CAMERA_POINTS[selectedIndex];
      // Original position (no zoom scale)
      const origRotated = rotateX(rotateY(cpBase.pos, orbitAngleY), orbitAngleX);
      const origP = project(origRotated, w, h, fov);
      // Zoomed position
      const zoomedPos = { x: cpBase.pos.x * zoomScale, y: cpBase.pos.y * zoomScale, z: cpBase.pos.z * zoomScale };
      const zoomedRotated = rotateX(rotateY(zoomedPos, orbitAngleY), orbitAngleX);
      const zoomedP = project(zoomedRotated, w, h, fov);

      if (origP && zoomedP) {
        // Ghost dot at original position
        ctx.save();
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(origP.x, origP.y, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Dashed line from ghost to zoomed point (only when zoom > 5, i.e. closer)
        if (zoomLevel > 5) {
          ctx.save();
          ctx.globalAlpha = 0.2;
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(origP.x, origP.y);
          ctx.lineTo(zoomedP.x, zoomedP.y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }
      }
    }

    // Draw 3D arrow from selected point to center of image
    if (selectedIndex !== null && selectedIndex < CAMERA_POINTS.length) {
      const cpBase = CAMERA_POINTS[selectedIndex];
      const cp = { ...cpBase, pos: { x: cpBase.pos.x * zoomScale, y: cpBase.pos.y * zoomScale, z: cpBase.pos.z * zoomScale } };
      const origin: Vec3 = { x: 0, y: 0, z: 0 };

      // Generate intermediate 3D points along the line, stopping at 60% of the way to center
      const segments = 20;
      const tMax = 0.6;
      const pathPts: { x: number; y: number }[] = [];
      for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * tMax;
        const pt3D: Vec3 = {
          x: cp.pos.x * (1 - t) + origin.x * t,
          y: cp.pos.y * (1 - t) + origin.y * t,
          z: cp.pos.z * (1 - t) + origin.z * t,
        };
        const rotated = rotateX(rotateY(pt3D, orbitAngleY), orbitAngleX);
        const p = project(rotated, w, h, fov);
        if (p) pathPts.push({ x: p.x, y: p.y });
      }

      if (pathPts.length >= 2) {
        // Draw the shaft as a gradient line
        ctx.save();
        ctx.globalAlpha = 0.5;
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(pathPts[0].x, pathPts[0].y);
        for (let i = 1; i < pathPts.length; i++) {
          ctx.lineTo(pathPts[i].x, pathPts[i].y);
        }
        ctx.stroke();
        ctx.restore();

        // Draw arrowhead at the end (pointing towards center)
        const tip = pathPts[pathPts.length - 1];
        const prev = pathPts[pathPts.length - 3] || pathPts[pathPts.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.5) {
          const nx = dx / len;
          const ny = dy / len;
          const arrowSize = 10;

          ctx.save();
          ctx.globalAlpha = 0.7;
          ctx.fillStyle = '#3b82f6';
          ctx.beginPath();
          ctx.moveTo(tip.x, tip.y);
          ctx.lineTo(tip.x - nx * arrowSize + ny * arrowSize * 0.45, tip.y - ny * arrowSize - nx * arrowSize * 0.45);
          ctx.lineTo(tip.x - nx * arrowSize - ny * arrowSize * 0.45, tip.y - ny * arrowSize + nx * arrowSize * 0.45);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      }
    }

    // Draw camera points
    for (const pp of projectedPoints) {
      const isSelected = pp.idx === selectedIndex;
      const baseRadius = isSelected ? 7 : 4.5;
      const r = baseRadius * Math.max(0.4, Math.min(1.2, pp.scale));

      // Depth-based alpha
      const alpha = Math.max(0.25, Math.min(1, 0.5 + pp.scale * 0.3));

      if (isSelected) {
        // Glow
        ctx.save();
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(pp.px, pp.py, r * 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Selected point
        ctx.fillStyle = '#3b82f6';
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(pp.px, pp.py, r, 0, Math.PI * 2);
        ctx.fill();

        // White inner dot
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(pp.px, pp.py, r * 0.4, 0, Math.PI * 2);
        ctx.fill();

        // Camera icon triangle
        const triSize = r * 0.7;
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.moveTo(pp.px - triSize * 0.5, pp.py - r - triSize * 0.3);
        ctx.lineTo(pp.px + triSize * 0.5, pp.py - r - triSize * 0.3);
        ctx.lineTo(pp.px, pp.py - r - triSize);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // Regular point
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#94a3b8';
        ctx.beginPath();
        ctx.arc(pp.px, pp.py, r, 0, Math.PI * 2);
        ctx.fill();

        // Subtle border
        ctx.strokeStyle = 'rgba(100,116,139,0.3)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }


  }, [orbitAngleY, orbitAngleX, imgLoaded, selectedIndex, zoomLevel]);

  // Resize canvas
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      // Store logical size for rendering and hit testing
      canvas.dataset.logicalWidth = String(rect.width);
      canvas.dataset.logicalHeight = String(rect.height);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Render loop
  useEffect(() => {
    const loop = () => {
      render();
      animFrame.current = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(animFrame.current);
  }, [render]);

  // Mouse/touch interaction
  const getLogicalCoords = (canvas: HTMLCanvasElement, clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getLogicalCoords(canvas, e.clientX, e.clientY);
    const lw = parseFloat(canvas.dataset.logicalWidth || '0');
    const lh = parseFloat(canvas.dataset.logicalHeight || '0');
    const fov = 4;

    // Check if clicking on a point
    let hitIdx: number | null = null;
    let minDist = Infinity;

    for (let i = 0; i < CAMERA_POINTS.length; i++) {
      const cp = CAMERA_POINTS[i];
      const rotated = rotateX(rotateY(cp.pos, orbitAngleY), orbitAngleX);
      const p = project(rotated, lw, lh, fov);
      if (!p) continue;
      const dx = p.x - x;
      const dy = p.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const hitRadius = 18;
      if (dist < hitRadius && dist < minDist) {
        minDist = dist;
        hitIdx = i;
      }
    }

    if (hitIdx !== null) {
      setSelectedIndex(hitIdx);
      const cp = CAMERA_POINTS[hitIdx];
      onSelectionChange({
        horizontal_angle: cp.hAngle,
        vertical_angle: cp.vAngle,
        zoom: zoomLevel,
      });
      // Reset zoom when selecting a new point
      setZoomLevel(5);
      return;
    }

    // Start orbit drag
    isDragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    canvas.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDragging.current || disabled) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };

    setOrbitAngleY(prev => prev + dx * 0.008);
    setOrbitAngleX(prev => Math.max(-0.8, Math.min(0.8, prev + dy * 0.008)));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    isDragging.current = false;
    const canvas = canvasRef.current;
    if (canvas) canvas.releasePointerCapture(e.pointerId);
  };

  return (
    <div ref={containerRef} className="w-full h-full relative select-none" style={{ minHeight: 220 }}>
      <canvas
        ref={canvasRef}
        className="w-full h-full rounded-2xl cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
      {/* Zoom controls */}
      {selectedIndex !== null && (
        <div className="absolute top-3 right-3 flex flex-col items-center gap-1">
          <span className="text-[9px] font-medium text-gray-400 mb-0.5">Zoom</span>
          <button
            type="button"
            onClick={() => {
              const newZoom = Math.min(10, zoomLevel + 1);
              setZoomLevel(newZoom);
              if (selectedIndex !== null && selectedIndex < CAMERA_POINTS.length) {
                const cp = CAMERA_POINTS[selectedIndex];
                onSelectionChange({ horizontal_angle: cp.hAngle, vertical_angle: cp.vAngle, zoom: newZoom });
              }
            }}
            disabled={disabled || zoomLevel >= 10}
            className="w-8 h-8 bg-white/90 hover:bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-lg font-medium"
            aria-label="Acercar zoom"
          >
            +
          </button>
          <div className="w-8 h-5 flex items-center justify-center">
            <span className="text-[10px] font-medium text-gray-500">{zoomLevel === 5 ? '0' : zoomLevel > 5 ? `+${zoomLevel - 5}` : `${zoomLevel - 5}`}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              const newZoom = Math.max(0, zoomLevel - 1);
              setZoomLevel(newZoom);
              if (selectedIndex !== null && selectedIndex < CAMERA_POINTS.length) {
                const cp = CAMERA_POINTS[selectedIndex];
                onSelectionChange({ horizontal_angle: cp.hAngle, vertical_angle: cp.vAngle, zoom: newZoom });
              }
            }}
            disabled={disabled || zoomLevel <= 0}
            className="w-8 h-8 bg-white/90 hover:bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-lg font-medium"
            aria-label="Alejar"
          >
            −
          </button>
        </div>
      )}
      {/* Selected angle label at bottom */}
      {selectedIndex !== null && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="px-3 py-1.5 bg-blue-500/90 backdrop-blur-sm rounded-full text-[11px] text-white font-semibold">
            {CAMERA_POINTS[selectedIndex].label}
          </div>
        </div>
      )}
      {/* Hint overlay */}
      {selectedIndex === null && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="px-3 py-1.5 bg-gray-100 backdrop-blur-sm rounded-full text-[11px] text-gray-500 font-medium">
            Arrastrá para rotar · Tocá un punto para seleccionar ángulo
          </div>
        </div>
      )}
    </div>
  );
};

export default AngleSelector3D;
